import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ShieldCheck, Check, Gift, Phone } from "lucide-react";

/**
 * Therapist verification request modal — opens from the Report header
 * when a Pro parent wants a therapist's stamp on the report.
 *
 * As of iter80 (Feb 2026) verification is FREE for selected Pro users
 * rather than a Razorpay ₹99 one-time charge. The payment step and the
 * "Pay ₹99" copy were removed; the CTA is now a plain "Request
 * verification" button that fires ``onConfirm`` directly. The parent
 * component (Report.jsx) still calls the same ``assessmentApi
 * .requestVerification`` endpoint, so no backend change is needed —
 * the therapist queue just picks up the request without a payment
 * cross-reference.
 *
 * Note: This modal is only mounted for ``plan === "pro"`` users.
 * Free-plan users see the ``UpgradeDialog`` instead (see the ternary
 * inside the verify-therapist-btn onClick in Report.jsx) so the
 * pricing/subscribe pitch still fires for the freemium tier.
 */
export default function VerifyPaymentModal({ open, onOpenChange, onConfirm, loading }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-cream rounded-3xl max-w-md p-0 overflow-hidden border-mint/20" data-testid="verify-pay-modal">
        <div className="bg-gradient-to-br from-mint to-mint-dark text-white p-6">
          <div className="flex items-center gap-2 mb-2">
            <ShieldCheck className="w-5 h-5" />
            <span className="text-sm font-semibold opacity-90">Report Verification</span>
          </div>
          <DialogHeader className="space-y-1 text-left">
            <DialogTitle className="font-heading text-3xl text-white">Get a therapist&apos;s stamp on your report</DialogTitle>
            <DialogDescription className="text-white/90 text-sm">A certified paediatric therapist will review your child&apos;s report and add their professional verification.</DialogDescription>
          </DialogHeader>
        </div>
        <div className="p-6 space-y-4">
          {/* FREE tier callout — replaces the old ₹99 payment card. The
              gift icon + Free copy is the clearest signal that Pro users
              are getting this at no extra charge. */}
          <div
            data-testid="verify-free-callout"
            className="rounded-2xl bg-white border-2 border-mint/25 p-4 flex items-center gap-3"
          >
            <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-mint/15 text-mint shrink-0">
              <Gift className="w-5 h-5" />
            </span>
            <div className="min-w-0">
              <div className="text-xs font-bold text-mint-dark uppercase tracking-widest">Free therapist verification</div>
              <p className="text-[13px] text-inkdark leading-snug">
                Available at no extra cost for selected Pro users on your current plan.
              </p>
            </div>
          </div>

          <ul className="space-y-2 text-sm text-inkdark">
            {[
              "Reviewed by a certified OT / paediatric specialist",
              "Verifier credentials displayed on your report",
              "Typical turn-around: 1–3 working days",
              "Free — no extra payment required",
            ].map((b) => (
              <li key={b} className="flex items-start gap-2"><Check className="w-4 h-4 text-mint mt-0.5 shrink-0" />{b}</li>
            ))}
          </ul>

          {/* Contact-consent note. Verified copy from product spec —
              parents need to know a therapist may reach out for
              clarification before they submit the request. */}
          <div
            data-testid="verify-contact-note"
            className="rounded-2xl bg-mint/5 border border-mint/25 p-4 flex items-start gap-3"
          >
            <Phone className="w-4 h-4 text-mint-dark mt-0.5 shrink-0" />
            <p className="text-[13px] text-inkdark leading-relaxed">
              To ensure the most accurate recommendations, our therapist may
              contact you by <strong>phone or message</strong> if any additional
              clarification is needed about your child&apos;s development or
              assessment responses.
            </p>
          </div>

          <Button
            onClick={onConfirm}
            disabled={loading}
            className="w-full bg-mint hover:bg-mint-dark text-white rounded-full py-6 font-bold"
            data-testid="verify-pay-confirm-btn"
          >
            {loading ? "Sending request…" : "Request verification"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
